import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector }                from 'react-redux';
import { sendEmail }                  from '../../store/thunks/leadsActions';
import request                        from '../../utils/request';

export default function EmailComposer({
  leadId,
  initialTo = '',
  subject: propSubject = '',
  threadId = null,
  campaignId = null,
  campaignLeadId = null,
  initialFrom = '',
  onClearTo,
  onSent,
}) {
  const dispatch = useDispatch();
  const adminInfo = useSelector(s => s.login.adminInfo || {});
  const aliasOptions = Array.from(new Set([
    initialFrom,
    adminInfo.gmail,
    adminInfo.gmailPrimaryAddress,
    adminInfo.email,
    ...(adminInfo.gmailAliases || []),
  ].filter(Boolean)));
  const [recipients, setRecipients]       = useState(initialTo);
  const [fromAddress, setFromAddress]     = useState(aliasOptions[0] || '');
  const [subject, setSubject]             = useState(propSubject);
  const [body, setBody]                   = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templates, setTemplates]         = useState([]);
  const [sending, setSending]             = useState(false);
  const [sent, setSent]                   = useState(false);

  // fetch templates
  useEffect(() => {
    request.get('/templates', { withCredentials: true })
      .then(res => setTemplates(res.data.templates || []))
      .catch(console.error);
  }, []);

  // sync props
  useEffect(() => { setRecipients(initialTo || ''); }, [initialTo]);
  useEffect(() => { setSubject(propSubject || ''); }, [propSubject]);
  useEffect(() => {
    if (aliasOptions[0]) {
      setFromAddress((current) => current || aliasOptions[0]);
    }
  }, [aliasOptions]);

  // apply template
  useEffect(() => {
    if (!selectedTemplateId) return;
    const tpl = templates.find(t => t._id === selectedTemplateId);
    if (tpl) {
      setSubject(tpl.subject || '');
      setBody(tpl.html   || '');
    }
  }, [selectedTemplateId, templates]);

 const isRawText = txt => !/<[a-z][\s\S]*>/i.test(txt);

  const handleSend = () => {
    setSending(true);

    // build HTML part: if raw text, turn \n into <br/>
    const htmlBody = isRawText(body)
      ? body.split('\n').map((line, i) => (
          // join using <br/> but avoid trailing
          i > 0 ? `<br/>${line}` : line
        )).join('')
      : body;

    // plain text just passes through
    const plainTextBody = body;

    dispatch(sendEmail({
      leadId,
      from:    fromAddress,
      to: recipients,
      subject,
      body: htmlBody,         // HTML version
      text: plainTextBody,    // Plain-text version (retains \n)
      threadId,
      campaignId,
      campaignLeadId,
    }))
      .unwrap()
      .then((result) => {
        setSent(true);
        setTimeout(() => setSent(false), 3000);
        // clear fields
        setRecipients('');
        setSubject('');
        setBody('');
        setSelectedTemplateId('');
        onClearTo?.();
        onSent?.({ status: 'contacted', result }); 
      })
      .catch(console.error)
      .finally(() => setSending(false));
  };


return (
  <div className="mb-6 space-y-4">
    {/* Success message */}
    {sent && (
      <div className="text-sm text-[#10B981] bg-[#ECFDF5] px-4 py-2 rounded-[12px] border border-[#D1FAE5]">
        Message sent!
      </div>
    )}

    {/* Template Selector */}
    <div>
      {aliasOptions.length > 0 && (
        <div className="mb-3">
          <label className="block text-sm font-semibold text-[#1F2937] mb-1">
            From
          </label>
          <select
            className="w-full p-3 border border-[#D1D5DB] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            value={fromAddress}
            onChange={e => setFromAddress(e.target.value)}
          >
            {aliasOptions.map(alias => (
              <option key={alias} value={alias}>{alias}</option>
            ))}
          </select>
        </div>
      )}
      <label className="block text-sm font-semibold text-[#1F2937] mb-1">
        Use Template
      </label>
      <select
        className="w-full p-3 border border-[#D1D5DB] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
        value={selectedTemplateId}
        onChange={e => setSelectedTemplateId(e.target.value)}
      >
        <option value="">Select a template</option>
        {templates.map(t => (
          <option key={t._id} value={t._id}>
            {t.name || t.subject}
          </option>
        ))}
      </select>
    </div>

    {/* To Field */}
    <div>
      <label className="block text-sm font-semibold text-[#1F2937] mb-1">To</label>
      <div className="flex">
        <input
          type="text"
          className="flex-1 p-3 border border-r-0 border-[#D1D5DB] rounded-l-[12px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
          value={recipients}
          onChange={e => setRecipients(e.target.value)}
          placeholder="Recipient email"
        />
        {recipients && (
          <button
            onClick={() => { setRecipients(''); onClearTo?.(); }}
            className="px-4 py-2 bg-[#F9FAFB] border border-[#D1D5DB] text-[#EF4444] rounded-r-[12px] hover:bg-[#F3F4F6]"
          >
            ×
          </button>
        )}
      </div>
    </div>

    {/* Subject Field */}
    <div>
      <input
        type="text"
        className="w-full p-3 border border-[#D1D5DB] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
        placeholder="Subject"
        value={subject}
        onChange={e => setSubject(e.target.value)}
      />
    </div>

    {/* Body Textarea */}
    <div>
      <textarea
        className="w-full p-3 border border-[#D1D5DB] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
        placeholder="Write your message…"
        rows={5}
        value={body}
        onChange={e => setBody(e.target.value)}
      />
    </div>

    {/* Live Preview */}
    {body && (
      <div className="bg-white border border-[#E5E7EB] rounded-[12px] p-4 shadow-sm">
        <h4 className="text-sm font-semibold text-[#1F2937] mb-2">Live Preview</h4>
        <div
          className="text-sm text-[#374151] whitespace-pre-line"
          dangerouslySetInnerHTML={{ __html: body }}
        />
      </div>
    )}

    {/* Send Button */}
    <button
      onClick={handleSend}
      disabled={sending}
      className={`
        w-full text-center px-4 py-3 rounded-[12px] font-medium text-white transition-colors duration-150
        ${sending ? 'bg-[#9CA3AF] cursor-not-allowed' : 'bg-[#2563EB] hover:bg-[#1D4ED8]'}
      `}
    >
      {sending ? 'Sending…' : 'Send'}
    </button>
  </div>
);

}
